if (!customElements.get('product-slider-thumbnails')) {
  /**
   *  @class
   *  @function ProductSliderThumbnails
   *
   *  Rubin (2026-10-19, GALERIE STABIL): product gallery with thumbnails.
   *  - Every slide is a normal Shopify <img> with src/srcset/sizes from
   *    image_url/image_tag (product-media.liquid). The browser shows it on its
   *    own; nothing here hides or fades a loaded image (a notice only covers
   *    the browser's broken-image icon while a failed image is retried).
   *  - Slides, thumbnails and the zoom are matched by media id
   *    (data-media-id), never by file name, position or count.
   *  - Loading: the first image is eager/high in the HTML. A selected slide is
   *    switched to eager/high at once; its neighbours follow (low) after it.
   *  - Each image a visitor waits for is watched: a failed or cancelled
   *    download (e.g. the page went into Safari's back/forward cache) is
   *    retried up to 3 times with a fresh URL; a download without any progress
   *    gets a fresh copy in the background; a download that progresses is never
   *    cut off. After that a message with a retry button replaces the empty
   *    field. Success always clears the error.
   */
  const RX_GALLERY = {
    maxAttempts: 3,
    backoff: [800, 2500, 6000],
    watchEvery: 1000,
    slowAfter: 2500,
    stallAfter: 15000,
    lateRetry: 65000,
    errorText: 'Bild konnte nicht geladen werden.',
    loadingText: 'Bild wird geladen …',
    retryText: 'Erneut versuchen'
  };

  class ProductSliderThumbnails extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('change', this.setupZoomListeners);
      // load/error do not bubble: listen in the capture phase for every image
      // inside the gallery, also for images added later.
      this.addEventListener('load', (event) => this.rxOnLoad(event.target), true);
      this.addEventListener('error', (event) => this.rxOnError(event.target), true);
      this.rxTimers = new Set();
    }
    connectedCallback() {
      if (this.rxDisconnectTimer) {
        clearTimeout(this.rxDisconnectTimer);
        this.rxDisconnectTimer = null;
      }
      // A gallery that is only moved in the DOM keeps its Flickity instance.
      if (this.flkty) {
        return;
      }
      this.product_container = this.closest('.thb-product-detail') || this.parentElement;
      this.thumbnail_container = this.product_container ? this.product_container.querySelector('.product-thumbnail-container') : null;
      this.video_containers = this.querySelectorAll('.product-single__media-external-video--play');

      this.setOptions();
      this.init();
    }
    disconnectedCallback() {
      // Removed for good (section re-rendered, product switched): drop the
      // slider, its listeners and timers. A plain move reconnects first.
      this.rxDisconnectTimer = setTimeout(() => {
        this.rxDisconnectTimer = null;
        if (!this.isConnected) {
          this.rxDestroy();
        }
      }, 0);
    }
    rxDestroy() {
      if (this.eventController) {
        this.eventController.abort();
        this.eventController = null;
      }
      if (this.rxLife) {
        this.rxLife.abort();
        this.rxLife = null;
      }
      if (this.rxObserver) {
        this.rxObserver.disconnect();
        this.rxObserver = null;
      }
      this.rxTimers.forEach((id) => clearTimeout(id));
      this.rxTimers.clear();
      this.querySelectorAll('.product-single__media-image img').forEach((img) => {
        this.rxUnwatch(img);
        this.rxClearRetry(img);
        this.rxDropProbe(img);
        if (img.rxLateTimer) {
          clearTimeout(img.rxLateTimer);
          img.rxLateTimer = null;
        }
      });
      if (this.flkty) {
        try {
          this.flkty.destroy();
        } catch (e) {}
        this.flkty = null;
      }
    }
    setOptions() {
      this.thumbnails = this.thumbnail_container ? this.thumbnail_container.querySelectorAll('.product-thumbnail') : [];
      this.prev_button = this.querySelector('.flickity-prev');
      this.next_button = this.querySelector('.flickity-next');
      this.options = {
        wrapAround: true,
        pageDots: false,
        contain: true,
        adaptiveHeight: true,
        initialIndex: '.is-initial-selected',
        prevNextButtons: false,
        fade: false,
        cellSelector: '.product-images__slide.is-active'
      };
    }
    init() {
      this.flkty = new Flickity(this, this.options);
      this.selectedIndex = this.flkty.selectedIndex;
      this.setupEvents();
      this.rxBindGlobal();
      this.setupZoomListeners();
      // Flickity fires no change event for the initial cell, so the active
      // thumbnail would otherwise start out of view on a long list.
      this.syncThumbnails(this.flkty.selectedIndex, 'auto');
      this.rxShow(this.flkty.selectedIndex);
    }
    reInit() {
      if (this.eventController) {
        this.eventController.abort();
      }

      this.flkty.destroy();
      this.setOptions();
      this.flkty = new Flickity(this, this.options);
      this.setupEvents();
      this.selectedIndex = this.flkty.selectedIndex;
      // A variant change marks the new cell as initial before rebuilding; no
      // select event is emitted while Flickity is rebuilt.
      this.syncThumbnails(this.flkty.selectedIndex);
      this.rxShow(this.flkty.selectedIndex);
    }
    setupEvents() {
      if (this.eventController) {
        this.eventController.abort();
      }
      this.eventController = new AbortController();
      const signal = this.eventController.signal;

      if (this.prev_button) {
        this.prev_button.addEventListener('click', () => {
          this.flkty.previous();
        }, { signal });
        this.prev_button.addEventListener('keyup', (event) => {
          this.flkty.previous();
          event.preventDefault();
        }, { signal });
      }
      if (this.next_button) {
        this.next_button.addEventListener('click', () => {
          this.flkty.next();
        }, { signal });
        this.next_button.addEventListener('keyup', (event) => {
          this.flkty.next();
          event.preventDefault();
        }, { signal });
      }
      this.video_containers.forEach((container) => {
        container.querySelector('button').addEventListener('click', function () {
          container.setAttribute('hidden', '');
        }, { signal });
      });
      this.flkty.on('settle', (index) => {
        this.selectedIndex = index;
      });
      // Every way of choosing a slide (thumbnail, swipe, arrows, keyboard,
      // zoom, variant change) ends in Flickity's select event.
      this.flkty.on('select', () => {
        this.rxShow(this.flkty.selectedIndex);
      });
      // A swipe shows its direction after a few pixels: start that slide now.
      this.flkty.on('dragStart', () => {
        this.rxDragPrimed = false;
      });
      this.flkty.on('dragMove', (event, pointer, moveVector) => {
        if (this.rxDragPrimed || !moveVector || Math.abs(moveVector.x) < 10) {
          return;
        }
        this.rxDragPrimed = true;
        this.rxLoadCell(this.flkty.selectedIndex + (moveVector.x < 0 ? 1 : -1), 'high');
      });
      // The first touch on the slider (a swipe is coming) prepares the
      // neighbours even if the first image is still downloading. Not on the
      // thumbnails: there the chosen slide must not share the line.
      const firstTouch = () => this.rxNeighbours(this.flkty.selectedIndex);
      ['pointerdown', 'touchstart'].forEach((type) => {
        this.addEventListener(type, firstTouch, { signal, passive: true, once: true });
      });
      this.flkty.on('change', (index) => {
        const previous_cell = this.flkty.cells[this.selectedIndex],
          previous_media = previous_cell ? previous_cell.element.querySelector('.product-single__media') : null;

        this.syncThumbnails(index);
        this.pauseMedia(previous_media);
      });

      // One delegated handler for all thumbnails (also ones rendered later).
      if (this.thumbnail_container) {
        // thumbnails drop their loading background once their picture is in
        const markThumb = (img) => {
          if (img && img.classList && img.classList.contains('rx-thumb__img') && img.complete && img.naturalWidth > 0) {
            img.classList.add('rx-thumb--loaded');
          }
        };
        this.thumbnail_container.addEventListener('load', (event) => markThumb(event.target), { signal, capture: true });
        this.thumbnail_container.querySelectorAll('img.rx-thumb__img').forEach(markThumb);
        this.thumbnail_container.addEventListener('click', (event) => {
          const thumbnail = event.target.closest('.product-thumbnail');
          if (thumbnail && this.thumbnail_container.contains(thumbnail)) {
            this.thumbnailClick(thumbnail);
          }
        }, { signal });
        this.thumbnail_container.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') {
            return;
          }
          const thumbnail = event.target.closest('.product-thumbnail');
          if (thumbnail && this.thumbnail_container.contains(thumbnail)) {
            event.preventDefault();
            this.thumbnailClick(thumbnail);
          }
        }, { signal });
      }
    }
    /* Media ids ---------------------------------------------------------- */
    mediaIdOf(element) {
      if (!element) {
        return '';
      }
      if (element.dataset.mediaId) {
        return element.dataset.mediaId;
      }
      return (element.id || '').replace(/^(Thumb|Slide)-/, '');
    }
    cellIndexOfMedia(mediaId) {
      if (!this.flkty || !mediaId) {
        return -1;
      }
      return this.flkty.cells.findIndex((cell) => this.mediaIdOf(cell.element) === mediaId);
    }
    thumbnailForMedia(mediaId) {
      if (!this.thumbnail_container || !mediaId) {
        return null;
      }
      return Array.from(this.thumbnail_container.querySelectorAll('.product-thumbnail')).find((thumb) => this.mediaIdOf(thumb) === mediaId) || null;
    }
    syncThumbnails(index, behavior = 'smooth') {
      if (!this.flkty || !this.flkty.cells[index] || !this.thumbnail_container) {
        return;
      }
      const active_thumb = this.thumbnailForMedia(this.mediaIdOf(this.flkty.cells[index].element));
      this.thumbnail_container.querySelectorAll('.product-thumbnail').forEach((item) => {
        const active = item === active_thumb;
        item.classList.toggle('is-initial-selected', active);
        item.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (active_thumb) {
        this.scrollToThumbnail(active_thumb, behavior);
      }
    }
    scrollToActiveThumbnail(behavior = 'smooth') {
      if (this.flkty) {
        this.syncThumbnails(this.flkty.selectedIndex, behavior);
      }
    }
    scrollToThumbnail(thumb, behavior = 'smooth') {
      requestAnimationFrame(() => {
        if (thumb.offsetParent === null) {
          return;
        }
        const windowHalfHeight = thumb.offsetParent.clientHeight / 2,
          windowHalfWidth = thumb.offsetParent.clientWidth / 2;
        thumb.parentElement.scrollTo({
          left: thumb.offsetLeft - windowHalfWidth + thumb.clientWidth / 2,
          top: thumb.offsetTop - windowHalfHeight + thumb.clientHeight / 2,
          behavior: behavior
        });
      });
    }
    pauseMedia(media) {
      if (!media) {
        return;
      }
      if (media.classList.contains('product-single__media-external-video')) {
        if (media.dataset.provider === 'youtube') {
          media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: ""
          }), "*");
        } else if (media.dataset.provider === 'vimeo') {
          media.querySelector('iframe').contentWindow.postMessage(JSON.stringify({
            method: "pause"
          }), "*");
        }
        media.querySelector('.product-single__media-external-video--play').removeAttribute('hidden');
      } else if (media.classList.contains('product-single__media-native-video')) {
        media.querySelector('video').pause();
      }
    }
    thumbnailClick(thumbnail) {
      if (!this.flkty) {
        return;
      }
      const index = this.cellIndexOfMedia(this.mediaIdOf(thumbnail));
      if (index < 0) {
        return;
      }
      if (index === this.flkty.selectedIndex) {
        // Same slide again: make sure it loads; a failed image gets a new try.
        this.rxShow(index);
        return;
      }
      this.flkty.select(index);
    }
    /* Loading ------------------------------------------------------------- */
    rxCell(index) {
      const length = this.flkty ? this.flkty.cells.length : 0;
      if (!length) {
        return null;
      }
      return this.flkty.cells[((index % length) + length) % length].element;
    }
    rxGalleryImages(root) {
      return Array.from((root || this).querySelectorAll('.product-single__media-image img'));
    }
    rxIsGalleryImage(img) {
      return !!img && img.tagName === 'IMG' && !!img.closest('.product-single__media-image') && this.contains(img);
    }
    rxIsLoaded(img) {
      return img.complete && img.naturalWidth > 0;
    }
    rxHasSource(img) {
      return !!(img.getAttribute('src') || img.getAttribute('srcset'));
    }
    rxBox(img) {
      return img.closest('.product-single__media-image');
    }
    rxSetState(img, state) {
      const box = this.rxBox(img);
      img.dataset.rxState = state;
      if (box) {
        box.dataset.rxState = state;
        if (state !== 'loading') {
          box.removeAttribute('data-rx-slow');
        }
      }
      if (state === 'error') {
        img.rxManual = false;
        this.rxOverlay(img, 'error');
      } else if (state === 'loading' && ((img.rxAttempts || 0) > 0 || img.rxManual) && !img.naturalWidth) {
        // a failed request is being repeated: cover the browser's broken-image
        // icon with a visible "loading" notice instead
        this.rxOverlay(img, 'retrying');
      } else {
        this.rxOverlay(img, null);
      }
    }
    // Show a slide: its images load now with high priority; an image that
    // failed before gets a new try because the visitor asked for it again.
    rxShow(index) {
      const cell = this.rxCell(index);
      if (!cell) {
        return;
      }
      this.rxLoadCell(index, 'high', true);
      const images = this.rxGalleryImages(cell);
      if (images.every((img) => this.rxIsLoaded(img))) {
        this.rxNeighbours(index);
      }
    }
    rxNeighbours(index) {
      if (!this.flkty || this.flkty.cells.length < 2) {
        return;
      }
      this.rxLoadCell(index + 1, 'low', false);
      this.rxLoadCell(index - 1, 'low', false);
    }
    rxLoadCell(index, priority, retryFailed) {
      const cell = this.rxCell(index);
      if (!cell) {
        return;
      }
      this.rxGalleryImages(cell).forEach((img) => this.rxPrepare(img, priority, retryFailed));
      cell.querySelectorAll('video[data-rx-poster]').forEach((video) => this.rxLoadPoster(video, priority));
    }
    rxPrepare(img, priority, retryFailed) {
      if (priority === 'high' || img.getAttribute('fetchpriority') !== 'high') {
        img.setAttribute('fetchpriority', priority);
      }
      // The src is in the HTML; switching a lazy image to eager starts it now.
      if (img.loading === 'lazy' || img.getAttribute('loading') === 'lazy') {
        img.loading = 'eager';
      }
      if (this.rxIsLoaded(img)) {
        this.rxLoaded(img);
        return;
      }
      if (!this.rxHasSource(img)) {
        return;
      }
      if (img.dataset.rxState === 'error') {
        if (retryFailed) {
          img.rxAttempts = 0;
          img.rxManual = true;
          this.rxRetry(img);
        }
        return;
      }
      if (img.dataset.rxState !== 'loading') {
        this.rxSetState(img, 'loading');
      }
      // the stall clock of a chosen image starts when it is chosen
      this.rxWatch(img, priority === 'high');
    }
    rxLoaded(img) {
      this.rxUnwatch(img);
      if (img.rxRetryTimer) {
        clearTimeout(img.rxRetryTimer);
        this.rxTimers.delete(img.rxRetryTimer);
        img.rxRetryTimer = null;
      }
      this.rxDropProbe(img);
      if (img.rxLateTimer) {
        clearTimeout(img.rxLateTimer);
        img.rxLateTimer = null;
      }
      img.rxAttempts = 0;
      img.rxManual = false;
      // always: also clears a notice or spinner left from an earlier state
      this.rxSetState(img, 'loaded');
    }
    rxOnLoad(img) {
      if (!this.rxIsGalleryImage(img) || !this.rxIsLoaded(img)) {
        return;
      }
      this.rxLoaded(img);
      if (this.flkty) {
        const cell = img.closest('.product-images__slide');
        const index = this.flkty.cells.findIndex((c) => c.element === cell);
        if (index === this.flkty.selectedIndex) {
          this.rxNeighbours(index);
        }
      }
    }
    rxOnError(img) {
      if (!this.rxIsGalleryImage(img) || !this.rxHasSource(img)) {
        return;
      }
      if (this.rxIsLoaded(img)) {
        // another candidate of the same image is already shown
        this.rxLoaded(img);
        return;
      }
      this.rxFail(img);
    }
    // Watch an image someone waits for: catches downloads that end without
    // load/error (cancelled when Safari puts the page into its back/forward
    // cache) and downloads that never start to arrive. A download that makes
    // progress (the browser already knows the picture's size) is never cut
    // off; for a selected image without any progress a fresh copy is tried in
    // the background and swapped in only when it has arrived.
    rxWatch(img, restart = false) {
      if (restart) {
        this.rxUnwatch(img);
      }
      if (img.rxWatchTimer) {
        return;
      }
      const started = Date.now();
      const tick = () => {
        this.rxTimers.delete(img.rxWatchTimer);
        img.rxWatchTimer = null;
        if (!img.isConnected) {
          return;
        }
        if (this.rxIsLoaded(img)) {
          this.rxOnLoad(img);
          return;
        }
        if (img.dataset.rxState !== 'loading' || img.rxRetryTimer) {
          return;
        }
        const waited = Date.now() - started;
        const box = this.rxBox(img);
        if (waited >= RX_GALLERY.slowAfter && box) {
          box.setAttribute('data-rx-slow', '');
        }
        if (img.complete && !img.naturalWidth && waited >= RX_GALLERY.watchEvery) {
          // finished without a picture and without an error event
          this.rxFail(img);
          return;
        }
        if (!img.naturalWidth && waited >= RX_GALLERY.stallAfter && this.rxIsSelected(img)) {
          if (!img.rxProbe) {
            this.rxStartProbe(img);
          } else if (Date.now() - img.rxProbeStarted >= RX_GALLERY.stallAfter) {
            // neither the original nor a fresh copy arrives: start over
            this.rxDropProbe(img);
            this.rxFail(img);
            return;
          }
        }
        img.rxWatchTimer = setTimeout(tick, RX_GALLERY.watchEvery);
        this.rxTimers.add(img.rxWatchTimer);
      };
      img.rxWatchTimer = setTimeout(tick, RX_GALLERY.watchEvery);
      this.rxTimers.add(img.rxWatchTimer);
    }
    rxIsSelected(img) {
      if (!this.flkty || !this.flkty.cells[this.flkty.selectedIndex]) {
        return false;
      }
      return this.flkty.cells[this.flkty.selectedIndex].element.contains(img);
    }
    // Fresh URLs for a new request: a new query value skips the browser's
    // cached failure; from the 2nd try on, a 1-2 px different width also asks
    // the CDN for a new size instead of its cached answer.
    rxFreshSources(img) {
      if (img.dataset.rxSrc === undefined) {
        img.dataset.rxSrc = img.getAttribute('src') || '';
        img.dataset.rxSrcset = img.getAttribute('srcset') || '';
      }
      const token = String(Date.now() % 1000000);
      const shift = Math.min(img.rxAttempts || 0, 3) > 1 ? (img.rxAttempts || 0) - 1 : 0;
      const bump = (url) => {
        if (!url) {
          return url;
        }
        let clean = url.replace(/([?&])rx_retry=\d+&?/, '$1').replace(/[?&]$/, '');
        if (shift) {
          clean = clean.replace(/([?&]width=)(\d+)/, (m, key, width) => key + Math.max(1, parseInt(width, 10) - shift));
        }
        return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'rx_retry=' + token;
      };
      const srcset = img.dataset.rxSrcset.split(',').map((part) => {
        const bits = part.trim().split(/\s+/);
        bits[0] = bump(bits[0]);
        return bits.join(' ');
      }).filter(Boolean).join(', ');
      return { srcset: srcset, src: img.dataset.rxSrc ? bump(img.dataset.rxSrc) : null };
    }
    rxStartProbe(img) {
      const fresh = this.rxFreshSources(img);
      const probe = new Image();
      img.rxProbe = probe;
      img.rxProbeStarted = Date.now();
      probe.onload = () => {
        if (img.rxProbe !== probe) {
          return;
        }
        img.rxProbe = null;
        if (!this.rxIsLoaded(img) && img.isConnected) {
          // the fresh copy is in the cache now: show it at once
          this.rxApplySources(img, fresh);
        }
      };
      probe.onerror = () => {
        if (img.rxProbe !== probe) {
          return;
        }
        img.rxProbe = null;
        if (!this.rxIsLoaded(img) && img.isConnected) {
          this.rxFail(img);
        }
      };
      if (img.getAttribute('sizes')) {
        probe.sizes = img.getAttribute('sizes');
      }
      if (fresh.srcset) {
        probe.srcset = fresh.srcset;
      }
      if (fresh.src) {
        probe.src = fresh.src;
      }
    }
    rxDropProbe(img) {
      if (img.rxProbe) {
        img.rxProbe.onload = null;
        img.rxProbe.onerror = null;
        img.rxProbe = null;
      }
    }
    rxClearRetry(img) {
      if (img.rxRetryTimer) {
        clearTimeout(img.rxRetryTimer);
        this.rxTimers.delete(img.rxRetryTimer);
        img.rxRetryTimer = null;
      }
    }
    rxUnwatch(img) {
      if (img.rxWatchTimer) {
        clearTimeout(img.rxWatchTimer);
        this.rxTimers.delete(img.rxWatchTimer);
        img.rxWatchTimer = null;
      }
    }
    rxFail(img) {
      this.rxUnwatch(img);
      this.rxDropProbe(img);
      if (img.rxRetryTimer) {
        return;
      }
      const attempts = (img.rxAttempts || 0) + 1;
      img.rxAttempts = attempts;
      if (attempts > RX_GALLERY.maxAttempts) {
        this.rxSetState(img, 'error');
        // The CDN keeps a "not found" for 60 s: one more quiet try after
        // that, if the visitor is still looking at this picture.
        if (!img.rxLateTimer) {
          img.rxLateTimer = setTimeout(() => {
            this.rxTimers.delete(img.rxLateTimer);
            img.rxLateTimer = null;
            if (img.isConnected && img.dataset.rxState === 'error' && this.rxIsSelected(img)) {
              img.rxAttempts = 0;
              img.rxManual = true;
              this.rxRetry(img);
            }
          }, RX_GALLERY.lateRetry);
          this.rxTimers.add(img.rxLateTimer);
        }
        return;
      }
      this.rxSetState(img, 'loading');
      const box = this.rxBox(img);
      if (box) {
        box.setAttribute('data-rx-slow', '');
      }
      img.rxRetryTimer = setTimeout(() => {
        this.rxTimers.delete(img.rxRetryTimer);
        img.rxRetryTimer = null;
        this.rxRetry(img);
      }, RX_GALLERY.backoff[attempts - 1] || 6000);
      this.rxTimers.add(img.rxRetryTimer);
    }
    // New request with a new URL: the browser may keep a failed answer (e.g.
    // a 404 for a just uploaded file is cached for 60 s), so reuse is useless.
    rxRetry(img) {
      if (!img.isConnected) {
        return;
      }
      this.rxClearRetry(img);
      this.rxUnwatch(img);
      this.rxDropProbe(img);
      this.rxApplySources(img, this.rxFreshSources(img));
    }
    rxApplySources(img, fresh) {
      // remembered so the attribute observer knows this change is ours
      img.rxOwnSrcset = fresh.srcset || img.getAttribute('srcset');
      img.rxOwnSrc = fresh.src || img.getAttribute('src');
      this.rxSetState(img, 'loading');
      img.loading = 'eager';
      if (fresh.srcset) {
        img.setAttribute('srcset', fresh.srcset);
      }
      if (fresh.src) {
        img.setAttribute('src', fresh.src);
      }
      this.rxWatch(img, true);
    }
    // Someone else replaced an image's source (other script, app, editor):
    // forget the old state, attempts and URLs and follow the new picture.
    rxObserveSources() {
      if (this.rxObserver || typeof MutationObserver === 'undefined') {
        return;
      }
      this.rxObserver = new MutationObserver((mutations) => {
        const changed = new Set();
        mutations.forEach((mutation) => {
          const img = mutation.target;
          if (this.rxIsGalleryImage(img)) {
            changed.add(img);
          }
        });
        changed.forEach((img) => {
          if (img.getAttribute('src') === img.rxOwnSrc && img.getAttribute('srcset') === img.rxOwnSrcset) {
            return;
          }
          img.rxOwnSrc = img.getAttribute('src');
          img.rxOwnSrcset = img.getAttribute('srcset');
          delete img.dataset.rxSrc;
          delete img.dataset.rxSrcset;
          img.rxAttempts = 0;
          this.rxUnwatch(img);
          this.rxClearRetry(img);
          this.rxDropProbe(img);
          if (!this.rxHasSource(img)) {
            this.rxSetState(img, 'idle');
            return;
          }
          if (this.rxIsSelected(img)) {
            img.loading = 'eager';
          }
          this.rxSetState(img, 'loading');
          this.rxWatch(img, true);
        });
      });
      this.rxObserver.observe(this, { subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
    }
    rxOverlay(img, mode) {
      const box = this.rxBox(img);
      if (!box) {
        return;
      }
      let message = box.querySelector('.rx-media__error');
      if (!mode || (mode === 'retrying' && img.naturalWidth > 0)) {
        if (message) {
          message.hidden = true;
        }
        return;
      }
      if (!message) {
        message = document.createElement('div');
        message.className = 'rx-media__error';
        const text = document.createElement('p');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'rx-media__retry';
        button.textContent = this.dataset.rxRetryText || RX_GALLERY.retryText;
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          img.rxAttempts = 0;
          img.rxManual = true;
          this.rxRetry(img);
        });
        message.append(text, button);
        box.appendChild(message);
      }
      message.dataset.mode = mode;
      message.setAttribute('role', mode === 'error' ? 'alert' : 'status');
      message.querySelector('p').textContent = mode === 'error' ? (this.dataset.rxErrorText || RX_GALLERY.errorText) : (this.dataset.rxLoadingText || RX_GALLERY.loadingText);
      message.querySelector('button').hidden = mode !== 'error';
      message.hidden = false;
    }
    // Back from Safari's back/forward cache, back online or back in the tab:
    // check every image that was started and resume what is missing.
    rxRecover(reason) {
      if (!this.flkty) {
        return;
      }
      const cellImages = [];
      this.flkty.cells.forEach((cell) => cellImages.push(...this.rxGalleryImages(cell.element)));
      cellImages.forEach((img) => {
        if (this.rxIsLoaded(img)) {
          if (img.dataset.rxState && img.dataset.rxState !== 'loaded') {
            this.rxLoaded(img);
          }
          return;
        }
        const state = img.dataset.rxState;
        if (state === 'error' && reason !== 'visible') {
          img.rxAttempts = 0;
          img.rxManual = true;
          this.rxRetry(img);
        } else if (state === 'loading' && !img.rxRetryTimer) {
          if ((img.complete && !img.naturalWidth) || (reason === 'pageshow' && !img.naturalWidth && this.rxIsSelected(img))) {
            // the request was dropped while the page was away
            img.rxAttempts = 0;
            this.rxRetry(img);
          } else {
            this.rxWatch(img, true);
          }
        }
      });
      this.rxShow(this.flkty.selectedIndex);
    }
    rxBindGlobal() {
      if (this.rxLife) {
        return;
      }
      this.rxLife = new AbortController();
      const signal = this.rxLife.signal;
      this.rxObserveSources();
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          this.rxRecover('pageshow');
        }
      }, { signal });
      window.addEventListener('online', () => this.rxRecover('online'), { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.rxRecover('visible');
        }
      }, { signal });
      document.addEventListener('product:variant-change', (e) => {
        if (e.detail && e.detail.variantTitle && e.detail.variantTitle !== 'Default Title') {
          this.dataset.variantTitle = e.detail.variantTitle;
          this.updatePswpProductInfo();
        }
      }, { signal });
    }
    rxLoadPoster(video, priority) {
      if (priority === 'high') {
        video.preload = 'metadata';
      }
      if (video.dataset.rxPosterStarted) {
        return;
      }
      video.dataset.rxPosterStarted = '1';
      const url = video.dataset.rxPoster,
        box = video.closest('.rx-lqip'),
        poster = new Image();
      const apply = () => {
        video.setAttribute('poster', url);
        if (box) {
          box.dataset.rxState = 'loaded';
        }
      };
      poster.onload = apply;
      poster.onerror = apply;
      poster.src = url;
    }
    setDraggable(draggable) {
      this.flkty.options.draggable = draggable;
      this.flkty.updateDraggable();
    }
    selectCell(mediaId) {
      if (!this.flkty) {
        return;
      }
      this.flkty.selectCell(mediaId);
      // make sure the (possibly unchanged) current slide is loading
      this.rxShow(this.flkty.selectedIndex);
    }
    /* Zoom ---------------------------------------------------------------- */
    setupZoomListeners() {
      if (!this.querySelectorAll('.product-single__media-zoom').length) {
        return;
      }
      this.setEventListeners();
    }
    buildItems() {
      const cells = this.flkty ? this.flkty.cells.map((cell) => cell.element) : Array.from(this.querySelectorAll('.product-images__slide.is-active'));
      const items = [];
      this.activeImages = [];
      cells.forEach((cell) => {
        const item = cell.querySelector('.product-single__media-image'),
          activelink = item ? item.querySelector('.product-single__media-zoom') : null;
        if (!activelink) {
          return;
        }
        const slideImage = item.querySelector('img');
        activelink.dataset.index = items.length;
        this.activeImages.push(item);
        items.push({
          src: activelink.getAttribute('href'),
          // open the zoom on the sharp image already shown in the slide
          msrc: slideImage && this.rxIsLoaded(slideImage) ? slideImage.currentSrc : activelink.dataset.msrc,
          thumbSrc: activelink.dataset.thumbSrc || activelink.dataset.msrc,
          w: activelink.dataset.w,
          h: activelink.dataset.h,
          mediaId: this.mediaIdOf(cell)
        });
      });
      return items;
    }
    setEventListeners() {
      this.links = this.querySelectorAll('.product-single__media-zoom');
      this.pswpElement = document.querySelectorAll('.pswp')[0];
      this.pswpOptions = {
        maxSpreadZoom: 2,
        loop: true,
        allowPanToNext: true,
        closeOnScroll: false,
        showHideOpacity: false,
        arrowKeys: true,
        history: false,
        captionEl: false,
        fullscreenEl: false,
        zoomEl: false,
        shareEl: false,
        counterEl: true,
        arrowEl: true,
        preloaderEl: true,
        getThumbBoundsFn: () => {
          const thumbnail = this.querySelector('.product-images__slide.is-selected'),
            pageYScroll = window.pageYOffset || document.documentElement.scrollTop,
            rect = thumbnail.getBoundingClientRect();
          return {
            x: rect.left,
            y: rect.top + pageYScroll,
            w: rect.width
          };
        }
      };

      this.links.forEach((link) => {
        // one click handler per zoom link, however often this runs
        if (link.rxZoomBound) {
          return;
        }
        link.rxZoomBound = true;
        link.addEventListener('click', (e) => this.zoomClick(e, link));
      });
    }
    buildPswpThumbnails(items) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      container.innerHTML = '';
      if (this.dataset.lightboxThumbnails === 'false' || items.length <= 1) {
        container.parentElement.style.display = 'none';
        return;
      }
      container.parentElement.style.display = '';
      items.forEach((item, index) => {
        const thumb = document.createElement('div');
        thumb.className = 'pswp__thumb';
        thumb.dataset.index = index;
        thumb.dataset.mediaId = item.mediaId || '';
        const img = document.createElement('img');
        img.src = item.thumbSrc;
        img.alt = '';
        img.loading = 'lazy';
        thumb.appendChild(img);
        container.appendChild(thumb);
      });
    }
    updatePswpProductInfo() {
      if (!this.pswpElement) return;
      const infoEl = this.pswpElement.querySelector('.pswp__product-info');
      if (!infoEl) return;
      if (this.dataset.lightboxProductInfo === 'false') {
        infoEl.style.display = 'none';
        return;
      }
      infoEl.style.display = '';
      const titleEl = infoEl.querySelector('.pswp__product-title');
      const variantEl = infoEl.querySelector('.pswp__variant-title');
      if (titleEl) titleEl.textContent = this.dataset.productTitle || '';
      if (variantEl) variantEl.textContent = this.dataset.variantTitle || '';
    }
    syncPswpThumbnails(index) {
      const container = this.pswpElement.querySelector('.pswp__thumbs-inner');
      if (!container) return;
      const thumbs = container.querySelectorAll('.pswp__thumb');
      thumbs.forEach((t, i) => {
        t.classList.toggle('is-active', i === index);
      });
      const activeThumb = container.querySelector('.pswp__thumb.is-active');
      if (activeThumb) {
        activeThumb.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
      }
    }
    zoomClick(e, link) {
      e.preventDefault();
      this.items = this.buildItems();
      this.pswpOptions.index = parseInt(link.dataset.index, 10) || 0;
      if (typeof PhotoSwipe === 'undefined' || !this.items.length) {
        return;
      }
      this.buildPswpThumbnails(this.items);
      this.updatePswpProductInfo();

      const pswpElement = this.pswpElement;
      const resetScroll = function () {
        const scrollWrap = pswpElement.querySelector('.pswp__scroll-wrap');
        if (scrollWrap) scrollWrap.scrollLeft = 0;
        pswpElement.scrollLeft = 0;
      };

      // The lightbox element is shared and outlives each PhotoSwipe instance —
      // bind its bar and thumbnail strip once; they act on the open instance.
      const bottomBar = pswpElement.querySelector('.pswp__bottom-bar');
      if (bottomBar && !bottomBar.rxBound) {
        bottomBar.rxBound = true;
        ['mousedown', 'touchstart', 'pointerdown'].forEach(evtName => {
          bottomBar.addEventListener(evtName, (evt) => evt.stopPropagation());
        });
      }
      const thumbsInner = pswpElement.querySelector('.pswp__thumbs-inner');
      if (thumbsInner && !thumbsInner.rxBound) {
        thumbsInner.rxBound = true;
        thumbsInner.addEventListener('click', function (evt) {
          evt.stopPropagation();
          const thumb = evt.target.closest('.pswp__thumb');
          if (thumb && pswpElement.rxPswp) {
            pswpElement.rxPswp.goTo(parseInt(thumb.dataset.index, 10));
            resetScroll();
          }
        });
      }

      let pswp = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, this.items, this.pswpOptions);
      pswpElement.rxPswp = pswp;
      const _this = this;

      pswp.listen('firstUpdate', function () {
        pswp.listen('parseVerticalMargin', function (item) {
          item.vGap = {
            top: 50,
            bottom: 140
          };
        });
      });

      pswp.listen('beforeChange', function () {
        const index = pswp.getCurrentIndex();
        _this.syncPswpThumbnails(index);
        resetScroll();
        // zoom items are images only: find the slide by media id, not position
        const item = _this.items[index];
        const cellIndex = item ? _this.cellIndexOfMedia(item.mediaId) : -1;
        if (_this.flkty && cellIndex > -1) {
          _this.flkty.select(cellIndex);
        }
      });

      pswp.listen('close', function () {
        const container = pswpElement.querySelector('.pswp__thumbs-inner');
        if (container) container.innerHTML = '';
        if (pswpElement.rxPswp === pswp) pswpElement.rxPswp = null;
      });

      pswp.init();
      this.syncPswpThumbnails(this.pswpOptions.index);
    }
  }
  customElements.define('product-slider-thumbnails', ProductSliderThumbnails);
}
